import {
  AwaitContainer,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Input,
  Table,
  TableColumn,
} from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IntegrationLogStatus,
  IntegrationMode,
  IntegrationRequestLogItem,
  useIntegrationRequestLog,
} from '@/entities/integrations';

type Props = {
  code: string;
};

const PER_PAGE = 50;

type Filters = {
  method: string;
  status: IntegrationLogStatus | '';
  mode: IntegrationMode | '';
};

const EMPTY_FILTERS: Filters = { method: '', status: '', mode: '' };

export const LoggingTab: FC<Props> = ({ code }) => {
  const { t } = useTranslation(['integrations']);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<IntegrationRequestLogItem | null>(null);

  const params = useMemo(
    () => ({
      limit: PER_PAGE,
      offset: (page - 1) * PER_PAGE,
      method: filters.method || undefined,
      status: filters.status || undefined,
      mode: filters.mode || undefined,
    }),
    [page, filters],
  );

  const { data, isLoading, isFetching } = useIntegrationRequestLog(code, params);

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('integrations:logging.filters.all') },
      { value: 'success', label: t('integrations:logging.filters.success') },
      { value: 'error', label: t('integrations:logging.filters.error') },
    ],
    [t],
  );

  const modeOptions = useMemo(
    () => [
      { value: '', label: t('integrations:logging.filters.all') },
      { value: 'mock', label: t('integrations:logging.filters.mock') },
      { value: 'production', label: t('integrations:logging.filters.production') },
    ],
    [t],
  );

  const columns: TableColumn<IntegrationRequestLogItem>[] = useMemo(
    () => [
      {
        accessorKey: 'createdAt',
        header: () => <span>{t('integrations:logging.table.createdAt')}</span>,
        size: 180,
        cell: ({ row }) => <span className="text-sm">{new Date(row.original.createdAt).toLocaleString()}</span>,
      },
      {
        accessorKey: 'method',
        header: () => <span>{t('integrations:logging.table.method')}</span>,
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.method}</span>,
      },
      {
        accessorKey: 'httpMethod',
        header: () => <span>{t('integrations:logging.table.httpMethod')}</span>,
        size: 90,
        cell: ({ row }) => <span className="text-sm">{row.original.httpMethod}</span>,
      },
      {
        accessorKey: 'responseStatus',
        header: () => <span>{t('integrations:logging.table.responseStatus')}</span>,
        size: 100,
        cell: ({ row }) => {
          const code = row.original.responseStatus;
          const isOk = code >= 200 && code < 300;
          return <Badge variant={isOk ? 'primary' : 'destructive'}>{code}</Badge>;
        },
      },
      {
        accessorKey: 'durationMs',
        header: () => <span>{t('integrations:logging.table.durationMs')}</span>,
        size: 110,
        cell: ({ row }) => <span className="text-sm">{row.original.durationMs}</span>,
      },
      {
        accessorKey: 'responseSizeBytes',
        header: () => <span>{t('integrations:logging.table.responseSizeBytes')}</span>,
        size: 110,
        cell: ({ row }) => <span className="text-sm">{row.original.responseSizeBytes ?? '—'}</span>,
      },
      {
        accessorKey: 'mode',
        header: () => <span>{t('integrations:logging.table.mode')}</span>,
        size: 110,
        cell: ({ row }) => (
          <Badge variant={row.original.mode === 'mock' ? 'primary' : 'secondary'}>{row.original.mode}</Badge>
        ),
      },
      {
        accessorKey: 'errorMessage',
        header: () => <span>{t('integrations:logging.table.errorMessage')}</span>,
        cell: ({ row }) => <span className="text-sm text-destructive">{row.original.errorMessage ?? ''}</span>,
      },
    ],
    [t],
  );

  const handleApply = () => setPage(1);
  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{t('integrations:logging.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            value={filters.method}
            onChange={(event) => setFilters((prev) => ({ ...prev, method: event.target.value }))}
            label={t('integrations:logging.filters.method')}
          />
          <Combobox
            value={filters.status}
            onChange={(value) => setFilters((prev) => ({ ...prev, status: (value as Filters['status']) ?? '' }))}
            options={statusOptions}
            label={t('integrations:logging.filters.status')}
          />
          <Combobox
            value={filters.mode}
            onChange={(value) => setFilters((prev) => ({ ...prev, mode: (value as Filters['mode']) ?? '' }))}
            options={modeOptions}
            label={t('integrations:logging.filters.mode')}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={handleReset}>
            {t('integrations:logging.filters.reset')}
          </Button>
          <Button onClick={handleApply}>{t('integrations:logging.filters.apply')}</Button>
        </div>

        <AwaitContainer isLoading={isLoading || isFetching}>
          {items.length === 0 ? (
            <div className="text-muted-foreground p-2">{t('integrations:logging.table.empty')}</div>
          ) : (
            <Table
              data={items}
              columns={columns}
              getKey={(row) => row.id}
              pagination={{ total, page, perPage: PER_PAGE }}
              type="paginated"
              onChangePagination={(nextPage) => setPage(nextPage)}
              styles={{ size: 'sm' }}
              onClickRow={setSelected}
            />
          )}
        </AwaitContainer>
      </CardContent>

      <DialogRoot
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('integrations:logging.details.title')}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="flex flex-col gap-3 text-sm">
              <DetailRow label={t('integrations:logging.details.url')} value={selected.url} mono />
              <DetailRow
                label={t('integrations:logging.table.method')}
                value={`${selected.httpMethod} ${selected.method}`}
                mono
              />
              <DetailRow
                label={t('integrations:logging.details.requestParams')}
                value={selected.requestParams ? JSON.stringify(selected.requestParams, null, 2) : '—'}
                mono
                multiline
              />
              {selected.pipelineRunId && (
                <DetailRow
                  label={t('integrations:logging.details.pipelineRunId')}
                  value={selected.pipelineRunId}
                  mono
                />
              )}
              {selected.scenarioCode && (
                <DetailRow label={t('integrations:logging.details.scenarioCode')} value={selected.scenarioCode} mono />
              )}
            </div>
          )}
          <DialogFooter>
            <div className="flex justify-end w-full">
              <Button variant="ghost" onClick={() => setSelected(null)}>
                {t('integrations:logging.details.close')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Card>
  );
};

type DetailRowProps = {
  label: string;
  value: string;
  mono?: boolean;
  multiline?: boolean;
};

const DetailRow: FC<DetailRowProps> = ({ label, value, mono, multiline }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-muted-foreground uppercase">{label}</span>
    {multiline ? (
      <pre className={`whitespace-pre-wrap ${mono ? 'font-mono' : ''} text-sm`}>{value}</pre>
    ) : (
      <span className={mono ? 'font-mono break-all' : ''}>{value}</span>
    )}
  </div>
);
